// Timed note events from a MusicXML score, for the etude audio player.
// Pitches are taken as written in <pitch>: for guitar the octave clef
// (clef-octave-change -1) only moves the display, so <pitch> already is
// the sounding pitch and no shift is applied here.

const ALTER_SUFFIX = { '-2': 'bb', '-1': 'b', '0': '', '1': '#', '2': '##' };

const childText = (el, tag) => {
  const child = el.getElementsByTagName(tag)[0];
  return child ? child.textContent.trim() : null;
};

const pitchName = (pitchEl) => {
  const step = childText(pitchEl, 'step');
  const octave = childText(pitchEl, 'octave');
  const alter = String(Math.round(parseFloat(childText(pitchEl, 'alter') || '0')));
  return `${step}${ALTER_SUFFIX[alter] ?? ''}${octave}`;
};

const hasTie = (noteEl, type) => Array.from(noteEl.getElementsByTagName('tie'))
  .some((t) => t.getAttribute('type') === type);

// Returns [{ note, startQuarters, durationQuarters }] sorted by start.
// Only the first part is read. Tied notes become one sustained event,
// and a note shared by two voices (same pitch, same onset) sounds once.
export const musicXmlToEvents = (xml) => {
  const doc = new DOMParser().parseFromString(xml, 'application/xml');
  const part = doc.getElementsByTagName('part')[0];
  if (!part) return [];

  const events = [];
  const openTies = new Map();
  let divisions = 1;
  let measureStart = 0;

  Array.from(part.getElementsByTagName('measure')).forEach((measure) => {
    let cursor = measureStart;
    let measureEnd = measureStart;
    let lastOnset = measureStart;

    Array.from(measure.children).forEach((el) => {
      if (el.tagName === 'attributes') {
        const d = childText(el, 'divisions');
        if (d) divisions = parseFloat(d);
        return;
      }
      if (el.tagName === 'backup' || el.tagName === 'forward') {
        const q = parseFloat(childText(el, 'duration') || '0') / divisions;
        cursor += el.tagName === 'backup' ? -q : q;
        measureEnd = Math.max(measureEnd, cursor);
        return;
      }
      if (el.tagName !== 'note') return;
      if (el.getElementsByTagName('grace').length) return;

      const quarters = parseFloat(childText(el, 'duration') || '0') / divisions;
      const isChordMember = el.getElementsByTagName('chord').length > 0;
      const onset = isChordMember ? lastOnset : cursor;
      if (!isChordMember) {
        lastOnset = cursor;
        cursor += quarters;
        measureEnd = Math.max(measureEnd, cursor);
      }

      const pitchEl = el.getElementsByTagName('pitch')[0];
      if (!pitchEl || el.getElementsByTagName('rest').length) return;
      const note = pitchName(pitchEl);

      const continued = hasTie(el, 'stop') ? openTies.get(note) : null;
      if (continued) {
        continued.durationQuarters = onset + quarters - continued.startQuarters;
      } else {
        events.push({ note, startQuarters: onset, durationQuarters: quarters });
      }
      const current = continued || events[events.length - 1];
      if (hasTie(el, 'start')) openTies.set(note, current);
      else openTies.delete(note);
    });

    measureStart = measureEnd;
  });

  const unique = new Map();
  events.forEach((e) => {
    const key = `${e.note}@${e.startQuarters.toFixed(6)}`;
    const prev = unique.get(key);
    if (!prev || e.durationQuarters > prev.durationQuarters) unique.set(key, e);
  });
  return Array.from(unique.values()).sort((a, b) => a.startQuarters - b.startQuarters);
};
